  import { useState, useEffect } from "react";
  import { useNavigate, useParams } from "react-router-dom";
  import { useForm } from "react-hook-form";
  import * as yup from "yup";
  import { yupResolver } from "@hookform/resolvers/yup";

  import { ContenedorPageTemplate } from "../../../components/templates/ContenedorPageTemplate";
  import {
    showUpdateAlert,
    showActualizadoCorrectamenteAlert,
    showErrorAlert,
  } from "../../../components/molecules/ShowAlert";

  import { actualizarPersona } from "../../../services/persona.service";
  import { actualizarDocente, getOneDocente } from "../../../services/docente.service";
  import { getAllProvinciasByIdDepartamento } from "../../../services/provincia.service";
  import { getAllDepartamentosByIdPais } from "../../../services/departamento.service";
  import { getAllPaises } from "../../../services/pais.service";
  import { getAllIdiomas, getAllIdiomasByIdDocente } from "../../../services/idioma.service";
  import { crearDocenteIdioma } from "../../../services/docenteIdioma.service";
  import { generosPersona } from "../../../utils/constantes";

  const inputStyle = {
    width: "100%",
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    marginBottom: "20px",
    fontSize: "16px",
    transition: "all 0.3s ease",
    backgroundColor: "#fff",
  };

  const formSectionStyle = {
    marginBottom: "30px",
    padding: "25px",
    borderRadius: "12px",
    background: "#ffffff",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  };

  const buttonStyle = {
    padding: "12px 24px",
    fontSize: "16px",
    fontWeight: "600",
    borderRadius: "8px",
    cursor: "pointer",
    border: "none",
    transition: "all 0.3s ease",
  };

  const saveButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#4c51bf",
    color: "#fff",
    boxShadow: "0 4px 15px rgba(76, 81, 191, 0.3)",
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#e53e3e",
    color: "#fff",
    boxShadow: "0 4px 15px rgba(229, 62, 62, 0.3)",
  };

  const validationSchema = yup.object().shape({
    nombre: yup.string(),
    apellido: yup.string(),
    ci: yup.string(),
    celular: yup
      .string(),
    correo: yup
      .string(),
    idDepartamento: yup.string(),
    idProvincia: yup.string(),
    fechaNacimiento: yup.string(),
    idiomas: yup.array().of(yup.string()).min(1, "Selecciona al menos un idioma."),
    cv: yup
      .mixed()
      .test("fileFormat", "Solo se permiten archivos PDF.", (value) => {
        if (!value) return true;
        const fileExtension = value?.name?.split(".").pop()?.toLowerCase();
        return fileExtension === "pdf";
      }),
  });

  export const ActualizarDocentePage = () => {
    const { idDocente } = useParams();
    const navigate = useNavigate();

    const [departamentos, setDepartamentos] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [idiomas, setIdiomas] = useState([]);
    const [idiomasDocenteActuales, setIdiomasDocenteActuales] = useState([]); // Para eliminar después
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
      register,
      handleSubmit,
      setValue,
      watch,
      formState: { errors },
    } = useForm({
      resolver: yupResolver(validationSchema),
    });

    const idDepartamento = watch("idDepartamento");
    const idProvincia = watch("idProvincia");
    const idiomasSeleccionados = watch("idiomas");

    // Registrar manualmente los campos controlados
    useEffect(() => {
      register("idDepartamento");
      register("idProvincia");
      register("idiomas");
    }, [register]);

    useEffect(() => {
      const fetchData = async () => {
        try {
          const docenteResponse = await getOneDocente(idDocente);
          
          const paises = await getAllPaises();
          const paisBolivia = paises.find((p) => p.nombre === "Bolivia");
          if (!paisBolivia) throw new Error("No se encontró el país Bolivia.");

          const idPais = paisBolivia.idPais;
          
          // Cargar datos en paralelo
          const [departamentosList, provinciasList, idiomasList, idiomasDocenteResponse] = await Promise.all([
            getAllDepartamentosByIdPais(idPais),
            getAllProvinciasByIdDepartamento(docenteResponse.persona.idDepartamento),
            getAllIdiomas(),
            getAllIdiomasByIdDocente(idDocente)
          ]);

          setDepartamentos(departamentosList);
          setProvincias(provinciasList);
          setIdiomas(idiomasList);
          
          // Guardar los IDs de idiomas actuales para comparar después
          const idsIdiomasActuales = idiomasDocenteResponse?.map(i => i.idIdioma) || [];
          setIdiomasDocenteActuales(idsIdiomasActuales);

          setValue("idPersona", docenteResponse.persona.idPersona);
          setValue("nombre", docenteResponse.persona.nombre);
          setValue("apellido", docenteResponse.persona.apellido);
          setValue("celular", docenteResponse.persona.celular);
          setValue("correo", docenteResponse.persona.correo);
          setValue("ci", docenteResponse.persona.ci);
          setValue("fechaNacimiento", docenteResponse.persona.fechaNacimiento?.split("T")[0]);
          setValue("idDepartamento", docenteResponse.persona.idDepartamento);
          setValue("idProvincia", docenteResponse.persona.idProvincia);
          setValue("sexo", docenteResponse.persona.sexo);
          
          // ✅ Cargar idiomas del docente desde el endpoint específico
          const idiomasDocente = idiomasDocenteResponse?.map(i => i.idIdioma) || [];
          setValue("idiomas", idiomasDocente);
        } catch (error) {
          console.error("❌ Error al cargar docente:", error);
          showErrorAlert("Error al cargar los datos del docente.");
        }
      };

      fetchData();
    }, [idDocente, setValue]);

    useEffect(() => {
      if (idDepartamento && provincias.length === 0) {
        const fetchProvincias = async () => {
          try {
            const provinciasList = await getAllProvinciasByIdDepartamento(idDepartamento);
            setProvincias(provinciasList);
          } catch (error) {
            showErrorAlert("Error al cargar las provincias.");
          }
        };
        fetchProvincias();
      }
    }, [idDepartamento]);


    const onSubmit = async (data) => {
      setIsSubmitting(true);

      try {
        // 1. Actualizar datos de persona (SIN idiomas)
        const personaPayload = {
          idPersona: data.idPersona,
          nombre: data.nombre,
          apellido: data.apellido,
          celular: data.celular,
          correo: data.correo,
          ci: data.ci,
          fechaNacimiento: new Date(data.fechaNacimiento).toISOString(),
          idDepartamento: data.idDepartamento,
          idProvincia: data.idProvincia,
          sexo: data.sexo,
        };

        await actualizarPersona(data.idPersona, personaPayload);

        // 2. Actualizar idiomas del docente - solo agregar los nuevos
        const idiomasNuevos = data.idiomas || [];
        const idiomasAntiguos = idiomasDocenteActuales;

        // Encontrar idiomas que hay que agregar (están en nuevos pero no en antiguos)
        const idiomasParaAgregar = idiomasNuevos.filter(id => !idiomasAntiguos.includes(id));

        // Agregar los nuevos idiomas
        for (const idIdioma of idiomasParaAgregar) {
          try {
            await crearDocenteIdioma({
              idDocente: idDocente,
              idIdioma: idIdioma
            });
          } catch (error) {
            console.error("Error al crear idioma:", error);
          }
        }

        const docentePayload = {
          idDocente: idDocente,
          cv: null,
        };

        if (data.cv) {
          const file = data.cv;
          const fileReader = new FileReader();

          fileReader.onloadend = async () => {
            const arrayBuffer = fileReader.result;
            const bytes = new Uint8Array(arrayBuffer);
            let binary = "";
            for (let i = 0; i < bytes.byteLength; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const base64String = window.btoa(binary);
            docentePayload.cv = base64String;

            await showUpdateAlert({
              callback: async () => {
                await actualizarDocente(idDocente, docentePayload);
                showActualizadoCorrectamenteAlert({
                  text: "Los datos del docente han sido actualizados correctamente.",
                  callback: () => navigate("/docentes"),
                });
              },
            });
          };

          fileReader.readAsArrayBuffer(file);
        } else {
          await showUpdateAlert({
            callback: async () => {
              await actualizarDocente(idDocente, docentePayload);
              showActualizadoCorrectamenteAlert({
                text: "Los datos del docente han sido actualizados correctamente.",
                callback: () => navigate("/docentes"),
              });
            },
          });
        }
      } catch (error) {
        showErrorAlert("Error al actualizar los datos.");
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleCancel = () => {
      navigate("/docentes");
    };

    return (
      <ContenedorPageTemplate titulo="Actualizar datos del Docente">
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            padding: "30px",
            background: "#f7fafc",
          }}
        >
          {/* Sección de Datos Personales */}
          <div style={formSectionStyle}>
            <h2
              style={{
                fontSize: "24px",
                marginBottom: "20px",
                color: "#2d3748",
                borderBottom: "2px solid #edf2f7",
                paddingBottom: "10px",
              }}
            >
              Datos Personales 👤
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Nombre:</label>
                <input type="text" {...register("nombre")} style={inputStyle} />
                {errors.nombre && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.nombre.message}</span>}
              </div>

              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Apellido:</label>
                <input type="text" {...register("apellido")} style={inputStyle} />
                {errors.apellido && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.apellido.message}</span>}
              </div>

              <div>
                <label htmlFor="ci" style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>CI:</label>
                <input id="ci" style={inputStyle} {...register("ci")} />
                {errors.ci && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.ci.message}</span>}
              </div>

              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Celular:</label>
                <input type="text" {...register("celular")} style={inputStyle} />
                {errors.celular && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.celular.message}</span>}
              </div>

              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Correo:</label>
                <input type="email" {...register("correo")} style={inputStyle} />
                {errors.correo && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.correo.message}</span>}
              </div>

              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Fecha de Nacimiento:</label>
                <input type="date" {...register("fechaNacimiento")} style={inputStyle} />
                {errors.fechaNacimiento && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.fechaNacimiento.message}</span>}
              </div>

              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Departamento:</label>
                <select 
                  style={inputStyle}
                  value={idDepartamento || ""}
                  onChange={(e) => setValue("idDepartamento", e.target.value, { shouldValidate: true })}
                >
                  <option value="">Selecciona un departamento</option>
                  {departamentos.map((dep) => (
                    <option key={dep.idDepartamento} value={dep.idDepartamento}>
                      {dep.nombre}
                    </option>
                  ))}
                </select>
                {errors.idDepartamento && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.idDepartamento.message}</span>}
              </div>

              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Provincia:</label>
                <select 
                  style={inputStyle}
                  value={idProvincia || ""}
                  onChange={(e) => setValue("idProvincia", e.target.value, { shouldValidate: true })}
                >
                  <option value="">Selecciona una provincia</option>
                  {provincias.map((prov) => (
                    <option key={prov.idProvincia} value={prov.idProvincia}>
                      {prov.nombre}
                    </option>
                  ))}
                </select>
                {errors.idProvincia && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.idProvincia.message}</span>}
              </div>

              <div>
                <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>Sexo:</label>
                <select {...register("sexo")} style={inputStyle}>
                  <option value="">Selecciona una opción</option>
                  {generosPersona.map((genero) => (
                    <option key={genero.value} value={genero.value}>
                      {genero.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Sección de Idiomas y CV */}
          <div style={formSectionStyle}>
            <h2
              style={{
                fontSize: "24px",
                marginBottom: "20px",
                color: "#2d3748",
                borderBottom: "2px solid #edf2f7",
                paddingBottom: "10px",
              }}
            >
              Idiomas y Documentos 🗣️
            </h2>
            
            <div>
              <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "15px" }}>
                Idiomas que domina:
              </label>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: "12px",
                padding: "15px",
                backgroundColor: "#f7fafc",
                borderRadius: "8px",
                border: "1px solid #e2e8f0"
              }}>
                {idiomas.map((idioma) => {
                  const isSelected = idiomasSeleccionados?.includes(idioma.idIdioma) || false;
                  return (
                    <label 
                      key={idioma.idIdioma}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px 15px",
                        backgroundColor: isSelected ? "#e6f7ff" : "#fff",
                        border: isSelected ? "2px solid #4c51bf" : "2px solid #e2e8f0",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        fontWeight: isSelected ? "600" : "400",
                        color: isSelected ? "#2d3748" : "#4a5568"
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "#cbd5e0";
                          e.currentTarget.style.backgroundColor = "#f7fafc";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = "#e2e8f0";
                          e.currentTarget.style.backgroundColor = "#fff";
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          const currentIdiomas = idiomasSeleccionados || [];
                          let newIdiomas;
                          
                          if (e.target.checked) {
                            newIdiomas = [...currentIdiomas, idioma.idIdioma];
                          } else {
                            newIdiomas = currentIdiomas.filter(id => id !== idioma.idIdioma);
                          }
                          
                          setValue("idiomas", newIdiomas, { shouldValidate: true });
                        }}
                        style={{
                          width: "18px",
                          height: "18px",
                          marginRight: "10px",
                          cursor: "pointer",
                          accentColor: "#4c51bf"
                        }}
                      />
                      <span style={{ fontSize: "15px" }}>
                        {isSelected ? "✓ " : ""}{idioma.nombre}
                      </span>
                    </label>
                  );
                })}
              </div>
              {errors.idiomas && <span style={{ color: "#e53e3e", fontSize: "14px", display: "block", marginTop: "8px" }}>{errors.idiomas.message}</span>}
              <div style={{ 
                marginTop: "12px", 
                padding: "10px 15px", 
                backgroundColor: idiomasSeleccionados?.length > 0 ? "#d4edda" : "#fff3cd",
                borderRadius: "6px",
                border: `1px solid ${idiomasSeleccionados?.length > 0 ? "#c3e6cb" : "#ffeaa7"}`,
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
                <span style={{ fontSize: "18px" }}>
                  {idiomasSeleccionados?.length > 0 ? "✅" : "⚠️"}
                </span>
                <span style={{ fontSize: "14px", color: "#2d3748", fontWeight: "500" }}>
                  {idiomasSeleccionados?.length > 0 
                    ? `${idiomasSeleccionados.length} idioma(s) seleccionado(s)`
                    : "Selecciona al menos un idioma"
                  }
                </span>
              </div>
            </div>

            <div style={{ marginTop: "25px" }}>
              <label style={{ fontWeight: "600", color: "#4a5568", display: "block", marginBottom: "8px" }}>
                CV (opcional, PDF):
              </label>
              <input
                type="file"
                accept="application/pdf"
                style={{
                  padding: "10px",
                  border: "2px dashed #cbd5e0",
                  borderRadius: "8px",
                  width: "100%",
                  cursor: "pointer",
                  backgroundColor: "#f7fafc"
                }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.type !== "application/pdf") {
                    alert("Solo se permiten archivos PDF.");
                    return;
                  }
                  setValue("cv", file);
                }}
              />
              {errors.cv && <span style={{ color: "#e53e3e", fontSize: "14px" }}>{errors.cv.message}</span>}
              <p style={{ fontSize: "14px", color: "#718096", marginTop: "8px" }}>
                📄 Solo archivos PDF
              </p>
            </div>
          </div>

          {/* Botones de acción */}
          <div style={{ 
            display: "flex", 
            justifyContent: "flex-end", 
            gap: "15px",
            marginTop: "30px"
          }}>
            <button 
              type="button" 
              style={{
                ...cancelButtonStyle,
                opacity: isSubmitting ? 0.6 : 1
              }}
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              style={{
                ...saveButtonStyle,
                opacity: isSubmitting ? 0.6 : 1
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Guardando..." : "💾 Guardar Cambios"}
            </button>
          </div>
        </form>
      </ContenedorPageTemplate>
    );
  };
